<template>
  <router-link custom :to="to">
    <div class="sampleCard">
      <div class="content" v-if="!isLoading">
        <div class="img">
          <img :src="img" alt="" />
          <img :src="secondImg" alt="" />
          <div class="trip-name-wrapper">
            <div>{{ dates }}</div>
            <div>{{ theme }}</div>
          </div>
        </div>
        <div class="info airline">
          <div class="top-label left">
            <strong>{{ flight }}</strong>
            <span>{{ flightClass }}</span>
          </div>
          <div class="bottom-label left">{{ price }}</div>
        </div>
        <div class="info hotel">
          <div class="top-label left">
            <strong>{{ hotel }}</strong>
          </div>
          <div class="bottom-label left">{{ hotelPrice }}</div>
        </div>
        <div class="info preferences">
          <div class="top-label left">Preferences matched</div>

          <div class="bottom-label left">
            <strong>{{ prefNumber }}</strong> matched places
          </div>
        </div>
        <div class="info action">
          <div>Customize Trip</div>
          <div class="price">{{ combinedPrice }}</div>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import {getReadableDuration, formatPrices} from '@mobi-components/amex-bluesky-components';
import {CurrencyCode} from '@mobi-components/global-solution';
import {getTripType, getTravelers} from '@/store/state/helpers';

export default {
  name: 'TripSampleCard',
  props: {
    trip: {
      type: Object,
      default: () => null,
    },
    to: String,
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedTags: Array,
  },
  methods: {
    formatPrice(prices) {
      const cost = formatPrices(prices, 'Points', 0);
      const usd = cost.labels['USD'];
      const usdValue = cost.values['USD'] || 0;
      const points = cost.values['AMEX_POINT'] || 0;
      const shortPoints =
        points / 1000 > 1
          ? `${usdValue ? '+' : ''}${Math.floor(points / 1000)}k Points`
          : cost.labels['AMEX_POINT'] || '';
      return {usd, points: shortPoints};
    },
  },
  computed: {
    img() {
      return (this.trip && this.trip.images && this.trip.images[1]) || '';
    },
    secondImg() {
      return (this.trip && this.trip.images && this.trip.images[2]) || '';
    },
    theme() {
      return (this.trip && this.trip.theme) || 'Build Your Own Trip';
    },
    dates() {
      const start = (this.trip && this.trip.scheduled_start_time) || null;
      const end = (this.trip && this.trip.scheduled_end_time) || null;
      if (!start || !end) {
        return '';
      }
      const startDate = new Date(start * 1000);
      const endDate = new Date(end * 1000);
      return getReadableDuration(startDate, endDate);
    },
    prefNumber() {
      const tripTags = this.trip.context.match.good_match_filters;

      const matchedTagsCount = this.selectedTags.filter((tag) => tripTags.find((tripTag) => tripTag.name === tag.name))
        .length;
      return matchedTagsCount;
    },
    flight() {
      return this.trip?.flights[0]?.transportation
        ? this.trip.flights[0]?.transportation.segments[0].carrier_name + ','
        : '';
    },
    flightClass() {
      return this.trip && this.trip.flights[0]?.transportation.segments[0].cabin_class;
    },
    hotel() {
      return this.trip && this.trip.accommodations[0].name;
    },
    hotelPrice() {
      const date1 = new Date(this.trip.scheduled_start_time);
      const date2 = new Date(this.trip.scheduled_end_time);

      const diffInMilliseconds = date2.getTime() - date1.getTime();
      const diffInDays = diffInMilliseconds / (60 * 60 * 24);
      const cost = formatPrices(
        [{...this.trip.prices[0], amount: this.trip.prices[0].amount / diffInDays}],
        'Points',
        0,
      ).labels[CurrencyCode.USD];

      return `${cost} Nightly, 1 Room`;
    },
    price() {
      if (!this.trip.prices[1]) return '';
      const cost = formatPrices([this.trip.prices[1]], 'Points', 0).labels[CurrencyCode.USD];

      const {adults, children} = getTravelers().filter;

      const adultsCount = +adults.split('')[0] || 0;
      const childrenCount = +children.split('')[0] || 0;

      const travelersCount = adultsCount + childrenCount;

      return `${cost} ${getTripType()}, ${travelersCount} ${travelersCount > 1 ? 'Travelers' : 'Traveler'}`;
    },
    combinedPrice() {
      return this.formatPrice(this.trip.prices).usd;
    },
  },
};
</script>

<style scoped>
.sampleCard.loading {
  position: relative;
  background-color: var(--dest-card-loading-color, #f1f1f1);
  overflow: hidden;
  border-radius: var(--card-br-large);
  box-shadow: none;
}
</style>

<style lang="scss" scoped>
.sampleCard {
  border-radius: var(--card-br-large);
  background-color: #f7f7f7;
  color: #fff;
  width: 300px;
  padding-left: 0;
  overflow: hidden;
  position: relative;
  display: block;
  border: none;
  appearance: none;
  text-align: left;
  box-shadow: 0px 0px 33px rgba(0, 0, 0, 0.13);
  padding-top: 128%;
  cursor: pointer;
}

.sampleCard img {
  width: 50%;
  height: 100%;
  object-fit: cover;
}
.sampleCard:hover::before {
  background-color: rgba(7, 29, 59, 0.4);
  height: 100%;
}

.content {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
}

.img {
  height: 150px;
  position: relative;
}

.img::before {
  content: '';
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 1;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.6));
  height: 100%;
}

.info {
  position: relative;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 40px;
  position: relative;
  background-position: 14px;
  background-size: 14px;
  padding-right: 14px;
}

.info + .info {
  border-top: 1px solid #f1f1f1;
}

.info:not(.action) {
  padding: 14px 0 14px 55px;
}

.info:not(.action)::before {
  content: '';
  position: absolute;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  top: 50%;
  left: 15px;
  transform: translate(0px, -50%);
  background-color: #f1f1f1;
  background-repeat: no-repeat;
  background-position: center;
}

.info.airline::before {
  background-image: url("data:image/svg+xml,%3Csvg width='21' height='22' viewBox='0 0 26 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.2914 14.6021L6.9495 14.3355L6.05878 16.0649L8.64644 17.1377L8.34086 17.8659L9.14706 18.6721L9.87525 18.3665L10.948 20.9542L12.6775 20.0635L12.4109 16.7346L12.4304 16.7216C13.1521 16.156 13.6527 15.7659 14.4459 15.0442L17.8853 20.1805L19.6147 18.4511L17.8008 12.5996L17.6122 11.9624L17.6382 11.9364L17.9243 11.6633C18.841 10.7921 19.5042 10.1679 19.9918 9.51778C20.629 8.66606 20.9996 7.80134 20.9996 7.13817C21.0061 6.90411 20.9411 6.67006 20.8175 6.47501C20.785 6.42299 20.7525 6.37748 20.707 6.33847C20.668 6.29946 20.6225 6.26045 20.5705 6.22794C20.3689 6.09791 20.1413 6.0394 19.9008 6.0459C19.2376 6.0459 18.3729 6.40999 17.5212 7.05365C16.871 7.54128 16.2404 8.20445 15.3692 9.12768L15.0766 9.43326L8.58792 7.41774L6.87148 9.13419L12.0078 12.5736C11.2796 13.3602 10.883 13.8674 10.3174 14.5891L10.2914 14.6021Z' fill='%23A3ABB6'/%3E%3C/svg%3E%0A");
}
.info.preferences::before {
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='12' viewBox='0 0 22 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='0.5' width='11.25' height='5.25' rx='2.57143' fill='%23A3ABB6'/%3E%3Crect x='14' width='7.5' height='5.25' rx='2.57143' fill='%23A3ABB6'/%3E%3Crect x='6.5' y='6.75' width='11.25' height='5.25' rx='2.57143' fill='%23A3ABB6'/%3E%3C/svg%3E%0A");
}
.info.hotel::before {
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='16' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M28 17.0312V26.3125C28 26.7601 27.8222 27.1893 27.5057 27.5057C27.1893 27.8222 26.7601 28 26.3125 28H24.625C24.1774 28 23.7482 27.8222 23.4318 27.5057C23.1153 27.1893 22.9375 26.7601 22.9375 26.3125V24.625H6.0625V26.3125C6.0625 26.7601 5.88471 27.1893 5.56824 27.5057C5.25178 27.8222 4.82255 28 4.375 28H2.6875C2.23995 28 1.81072 27.8222 1.49426 27.5057C1.17779 27.1893 1 26.7601 1 26.3125V17.0312C1 16.5837 1.17779 16.1545 1.49426 15.838C1.81072 15.5215 2.23995 15.3438 2.6875 15.3438H26.3125C26.7601 15.3438 27.1893 15.5215 27.5057 15.838C27.8222 16.1545 28 16.5837 28 17.0312ZM21.25 1H7.75C7.30245 1 6.87322 1.17779 6.55676 1.49426C6.24029 1.81072 6.0625 2.23995 6.0625 2.6875V4.375H4.375C3.92745 4.375 3.49822 4.55279 3.18176 4.86926C2.86529 5.18572 2.6875 5.61495 2.6875 6.0625V12.8125H6.0625V11.9688C6.0625 11.2974 6.32918 10.6536 6.80389 10.1789C7.27859 9.70418 7.92242 9.4375 8.59375 9.4375H10.2812C10.9526 9.4375 11.5964 9.70418 12.0711 10.1789C12.5458 10.6536 12.8125 11.2974 12.8125 11.9688V12.8125H16.1875V11.9688C16.1875 11.2974 16.4542 10.6536 16.9289 10.1789C17.4036 9.70418 18.0474 9.4375 18.7188 9.4375H20.4062C20.7387 9.4375 21.0678 9.50297 21.3749 9.63018C21.682 9.75739 21.9611 9.94384 22.1961 10.1789C22.4312 10.4139 22.6176 10.693 22.7448 11.0001C22.872 11.3072 22.9375 11.6363 22.9375 11.9688V12.8125H26.3125V6.0625C26.3125 5.61495 26.1347 5.18572 25.8182 4.86926C25.5018 4.55279 25.0726 4.375 24.625 4.375H22.9375V2.6875C22.9375 2.23995 22.7597 1.81072 22.4432 1.49426C22.1268 1.17779 21.6976 1 21.25 1Z' fill='%23A3ABB6'/%3E%3C/svg%3E%0A");
}
.info.action {
  background: var(--primary-color);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 17px 17px 13px;
}
.info .price {
  font-weight: 700;
}

.top-label {
  position: relative;
  display: inline-block;
  width: 230px;
  white-space: nowrap;
  font-style: normal;
  font-weight: 400;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: #071d3b;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.top-label > span {
  padding-left: 4px;
}

.bottom-label {
  font-style: normal;
  font-weight: 400;
  line-height: 118.14%;
  color: #596b8d;
  font-size: 12px;
}

.sampleCard {
  font-size: 1rem;
}

@media (min-width: 1600px) {
  .sampleCard {
    font-size: 1.1rem;
  }
}

@media (min-width: 2400px) {
  .sampleCard {
    font-size: 2rem;
  }
}

@media (min-width: 3400px) {
  .sampleCard {
    font-size: 3.5rem;
  }
}

.trip-name-wrapper {
  position: absolute;
  z-index: 1;
  bottom: 20px;
  left: 15px;
  right: 15px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  div:nth-of-type(1) {
    font-size: 12px;
  }

  div:nth-of-type(2) {
    font-size: 24px;
  }
}
</style>
